<script lang="ts" setup>
import { FormType } from '@/core/models/form-type.enum';
import { NxtInput, NxtRadioButtonGroup, NxtTextarea } from '@nxtpeople/nxt-styles';
import { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorSelect from './ColorSelect.vue';
import Dropdown from './Dropdown.vue';

const { t } = useI18n();

const emits = defineEmits(['update:modelValue']);
defineProps({
   formType: { type: String as PropType<FormType>, default: 'text' },

   label: { type: String, default: '' },
   modelValue: { type: [String, Number], default: null },
   name: { type: String, default: '' },
   errors: { type: Object, default: null },
   disabled: { type: Boolean, default: false },
   mode: { type: String, default: 'light' },
   required: { type: Boolean, default: false },

   //in case of dropdown
   options: { type: Array, default: null },
   optionsUrl: { type: String, default: null },
   displayKey: { type: String, default: 'name' },
   emitObject: { type: Boolean, default: false },
   customDisplayTemplate: { type: Function as PropType<(option: any) => string>, default: null },
   dropdownParams: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
});
</script>

<template>
   <div class="form-element">
      <NxtInput
         v-if="formType === FormType.Number || formType === FormType.Text"
         :label="label"
         :model-value="modelValue"
         :type="formType"
         :name="name"
         :errors="errors"
         :disabled="disabled"
         :mode="mode"
         :required="required"
         @update:model-value="emits('update:modelValue', $event)"
      />

      <NxtTextarea
         v-if="formType === FormType.TextArea"
         :label="label"
         :model-value="modelValue"
         :type="formType"
         :name="name"
         :errors="errors"
         :disabled="disabled"
         :mode="mode"
         :required="required"
         @update:model-value="emits('update:modelValue', $event)"
      />

      <ColorSelect
         v-else-if="formType === FormType.Color"
         :model-value="modelValue"
         :name="'color'"
         :show-label="false"
         :colors="options"
         :disabled="disabled"
         :default-color="'#E2E0EF'"
         :required="required"
         @update:model-value="emits('update:modelValue', $event)"
      />

      <NxtDatepicker
         v-if="formType === FormType.Date"
         :label="label"
         :placeholder="t('generic.dates.date')"
         :model-value="modelValue"
         :name="name"
         :errors="errors"
         :disabled="disabled"
         :mode="mode"
         :required="required"
         @update:model-value="emits('update:modelValue', $event)"
      />

      <Dropdown
         v-if="[FormType.Dropdown, FormType.DropdownSearch].includes(formType)"
         v-bind="dropdownParams"
         :name="name"
         :model-value="modelValue"
         :label="label"
         :options="options"
         :errors="errors"
         :disabled="disabled"
         :display-key="displayKey"
         :search-type="formType === FormType.DropdownSearch ? 'custom' : 'internal'"
         :url="optionsUrl"
         :emit-object="emitObject"
         :mode="mode"
         :required="required"
         @update:model-value="emits('update:modelValue', $event)"
      >
         <template v-if="customDisplayTemplate" #displayOption="{ option }">
            {{ customDisplayTemplate(option) }}
         </template>
         <template v-if="customDisplayTemplate" #option="{ option }">
            {{ customDisplayTemplate(option) }}
         </template>
      </Dropdown>
      <NxtRadioButtonGroup
         v-if="FormType.RadioButtons === formType"
         :name="name"
         :model-value="modelValue"
         :label="label"
         :disabled="disabled"
         :errors="errors"
         class="my-2"
         :options="options"
         :required="required"
         :direction="'horizontal'"
         @update:model-value="emits('update:modelValue', $event)"
      />
   </div>
</template>
